import React, { useState } from "react";
import { Tooltip } from "react-tooltip";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state";
import { TyreConfig } from "../../../../types";
import OneTyre from "../OneTyre";
import styles from "./style.module.scss";
import {
  DndContext,
  // DragOverlay,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { setTPMSConfig } from "../../../../state/tpmsReducer";
import { store } from "../../../../state";
import { MutateTPMSConfig } from "../../../../utils/graphql/hooks/TPMSHooks";
import { BsTrash3 } from "react-icons/bs";
import AddTyreDialog from "../AddTyreDialog";
import EditTyreDialog from "../EditTyreDialog";

interface TyreGroupProps {
  group: string;
  selectMode?: boolean;
  selectedTyres: string[];
  setSelectedTyres: React.Dispatch<React.SetStateAction<string[]>>;
}

export default function TyreGroup(props: TyreGroupProps) {
  const { group, selectMode, selectedTyres, setSelectedTyres } = props;
  const editModeState = useSelector(
    (state: RootState) => state.tpms.editModeState,
  );
  const tpmsConfig = useSelector((state: RootState) => state.tpms.tpmsConfig);
  const tyreConfig = tpmsConfig?.tyres || [];
  const selectedCar = useSelector((state: RootState) => state.tpms.selectedCar);
  const [isAddTyreDialogOpen, setAddTyreDialogOpen] = useState(false);
  const [isEditTyreDialogOpen, setEditTyreDialogOpen] = useState(false);
  const [selectedSensorId, setSelectedSensorId] = useState("");
  const [grabbedId, setGrabbedId] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const filteredTyreConfig = tyreConfig.filter((x) => {
    return (
      x.carId === selectedCar &&
      x.isFront === (group === "Front" ? true : false)
    );
  });

  const updateTPMSConfig = MutateTPMSConfig();

  React.useEffect(() => {
    if (!selectMode && selectedTyres.length > 0) {
      setSelectedTyres([]);
    }
  }, [selectMode, selectedTyres.length, setSelectedTyres]);

  function handleDragEnd(event: DragEndEvent) {
    setGrabbedId(null);
    const { active, over } = event;
    if (active && active.id) {
      if (document.querySelectorAll(".trashContainer:hover").length > 0) {
        const tyreObj = tyreConfig.find(
          (x) => x.sensorId === active.id,
        ) as TyreConfig;
        console.log("REMOVE", tyreObj.sensorId);
        const newTyreConfig = tyreConfig.filter(function (x) {
          return x.sensorId !== tyreObj.sensorId;
        });
        const newTPMSConfig = {
          tyres: newTyreConfig,
        };
        // store.dispatch(setTPMSConfig(newTPMSConfig)); //TODO: revert this if the mutation returns error
        updateTPMSConfig({
          variables: newTPMSConfig,
        });
        return;
      }

      if (over && active.id !== over.id) {
        const oldIndex = filteredTyreConfig
          .map((x: TyreConfig) => x.sensorId)
          .indexOf(active.id as string);
        const newIndex = filteredTyreConfig
          .map((x: TyreConfig) => x.sensorId)
          .indexOf(over.id as string);
        const newGroupSensorMap = arrayMove(
          filteredTyreConfig,
          oldIndex,
          newIndex,
        );
        const newTyreConfig = tyreConfig.filter((x) => {
          return !filteredTyreConfig
            .map((y) => {
              return y.sensorId;
            })
            .includes(x.sensorId);
        });
        for (const obj of newGroupSensorMap) {
          newTyreConfig.push(obj);
        }
        const newTPMSConfig = {
          tyres: newTyreConfig,
        };
        store.dispatch(setTPMSConfig(newTPMSConfig)); //TODO: revert this if the mutation returns error
        updateTPMSConfig({
          variables: newTPMSConfig,
        });
      }
    }
  }

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    setGrabbedId(active.id);
  }

  function handleClick(e: React.MouseEvent<HTMLDivElement>, sensorId: string) {
    const state = store.getState();
    if (selectMode) {
      const clickedTyre = tyreConfig.find((x) => x.sensorId === sensorId);
      if (!clickedTyre) return;
      const isFront = clickedTyre.isFront;
      const selectedFront = selectedTyres.filter((id) => {
        const t = tyreConfig.find((x) => x.sensorId === id);
        return t && t.isFront;
      });
      const selectedRear = selectedTyres.filter((id) => {
        const t = tyreConfig.find((x) => x.sensorId === id);
        return t && !t.isFront;
      });
      const isSelected = selectedTyres.includes(sensorId);
      if (isSelected) {
        setSelectedTyres((prev) => prev.filter((id) => id !== sensorId));
      } else {
        if (
          (isFront && selectedFront.length >= 2) ||
          (!isFront && selectedRear.length >= 2)
        ) {
          return;
        }
        setSelectedTyres((prev) => [...prev, sensorId]);
      }
      return;
    }
    if (state.tpms.editModeState && e.type === "contextmenu") {
      e.preventDefault();
      setSelectedSensorId(sensorId);
      setEditTyreDialogOpen(true);
    }
  }

  const items: string[] = filteredTyreConfig.map((x: TyreConfig) => {
    return x.sensorId;
  });

  return (
    <>
      <Tooltip id="trash" className={styles.tooltip} />
      <Tooltip id="maxTyres" className={styles.tooltip} />
      <div className={styles.tyreGroup}>
        <DndContext
          autoScroll={false}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items}>
            <div className={styles.draggableContainer}>
              {filteredTyreConfig.map((x: TyreConfig, index: number) => {
                const isSelected = selectedTyres.includes(x.sensorId);
                return (
                  <div
                    onClick={(e) => handleClick(e, x.sensorId)}
                    onContextMenu={(e) => handleClick(e, x.sensorId)}
                    key={x.sensorId}
                    className={
                      selectMode && !isSelected
                        ? styles.tyreSelectable
                        : undefined
                    }
                  >
                    <OneTyre
                      index={index}
                      group={group}
                      car={selectedCar}
                      selectMode={selectMode && !isSelected}
                    ></OneTyre>
                  </div>
                );
              })}
            </div>
          </SortableContext>
          {/* <DragOverlay>
          {grabbedId ? (
            <></>
          ) : // <OneTyre
          //   // frame={x.sensorData}
          //   // tyreID={x.tyreId}
          //   // index={index}
          // ></OneTyre>
          null}
        </DragOverlay> */}
        </DndContext>
      </div>
      {editModeState && (
        <>
          <div
            // ref={setNodeRef}
            className={`trashContainer ${styles.trashContainer} ${
              grabbedId ? styles.trashEnabled : null
            }`}
          >
            <div>
              <BsTrash3
                data-tooltip-id="trash"
                data-tooltip-html={
                  grabbedId ? "" : "Drag a tyre here to delete it"
                }
              ></BsTrash3>
            </div>
          </div>
          <div
            className={styles.addTyreButtonContainer}
            data-tooltip-id="maxTyres"
            data-tooltip-html={
              filteredTyreConfig.length >= 6 && selectedCar !== "DEV"
                ? "Maximum number of tyres<br />are already added (6)"
                : "Add tyre"
            }
          >
            <button
              onClick={() => setAddTyreDialogOpen(true)}
              className={styles.addTyreButton}
              disabled={filteredTyreConfig.length >= 6 && selectedCar !== "DEV"}
            >
              +
            </button>
          </div>
        </>
      )}
      <AddTyreDialog
        isOpen={isAddTyreDialogOpen}
        showDialog={setAddTyreDialogOpen}
        group={group}
      ></AddTyreDialog>
      <EditTyreDialog
        isOpen={isEditTyreDialogOpen}
        showDialog={setEditTyreDialogOpen}
        selectedSensorId={selectedSensorId}
        group={group}
      ></EditTyreDialog>
    </>
  );
}
